import {IonIcon, IonLabel, useIonViewWillLeave} from "@ionic/react";
import {cameraOutline} from "ionicons/icons";
import type {ID, IonButtonProps} from "app/utils/types";
import {useTranslation} from "react-i18next";
import {
  AddAttachmentDocument,
  type AttachmentCollectionFragment,
  type AttachmentCollectionIdFragment
} from "app/gql/graphqlSchema";
import {getUploadyDestination, query} from "app/gql/client";
import {
  type Batch,
  FILE_STATES,
  Uploady,
  useAbortAll,
  useBatchAddListener,
  useBatchFinishListener,
  useUploady
} from "@rpldy/uploady";
import {type FunctionComponent, useRef} from "react";
import PQueue from "p-queue";
import {zUploadResponse} from "app/utils/validator";
import {useLoadingContext} from "app/employee/LoadingContext";

type Props = {
  as: FunctionComponent<IonButtonProps>;
  attCollection: AttachmentCollectionFragment;
  onUploadCompleted?: () => void | Promise<void>;
  accept?: string;
} & Omit<IonButtonProps, "onClick">;

const addAttachments = async (
  collectionId: AttachmentCollectionIdFragment,
  stageUploadIds: ID[],
  signal?: AbortSignal
) => {
  const batchPromises = stageUploadIds.map((stagedUploadId) =>
    query(AddAttachmentDocument, {
      input: {
        collectionId,
        stagedUploadId
      },
      signal
    }).then((result) => result.attach)
  );
  const batchResult = await Promise.allSettled(batchPromises);
  for (const result of batchResult) {
    if (result.status === "fulfilled") {
      const attachment = result.value;
      if (attachment.__typename === "AttachmentSuccessResult") {
        console.log("Attachment added", attachment.attachment);
      } else {
        console.error("Attachment failed", attachment.errors);
      }
    } else {
      console.error("Attachment rejected", result.reason);
    }
  }
};

const collectFinishedUploads = (batch: Batch) => {
  const finishedUploads: ID[] = [];
  for (const item of batch.items) {
    if (item.state === FILE_STATES.FINISHED) {
      const uploadResponse = zUploadResponse.safeParse(item.uploadResponse);
      if (uploadResponse.success) {
        for (const responseData of uploadResponse.data.data) {
          finishedUploads.push(responseData.id.toString());
        }
      } else {
        console.error("Failed to parse upload response", uploadResponse.error);
      }
    }
  }
  return finishedUploads;
};

export const AddAttachment = (props: Props) => {
  const {accept} = props;
  return (
    <Uploady accept={accept} concurrent destination={getUploadyDestination()} maxConcurrent={2} method="POST" multiple>
      <AddAttachmentInner {...props} />
    </Uploady>
  );
};

const AddAttachmentInner = ({attCollection, onUploadCompleted, as: Component, ...props}: Omit<Props, "canUpload">) => {
  const {t} = useTranslation();
  const {startLoading, finishLoading} = useLoadingContext();
  const {showFileUpload} = useUploady();

  const queueRef = useRef(
    new PQueue({concurrency: 1}).on("error", (err) => {
      console.error("loadingQueue error", err);
    })
  );

  useBatchAddListener(() => {
    queueRef.current.add(() => startLoading());
  });

  useBatchFinishListener((batch) => {
    console.log("Finish", batch);
    queueRef.current.add(async () => {
      const finishedUploads = collectFinishedUploads(batch);
      await addAttachments(attCollection.id, finishedUploads);
      finishLoading();
      await onUploadCompleted?.();
    });
  });

  const abortAll = useAbortAll();

  useIonViewWillLeave(() => {
    abortAll();
    finishLoading();
  }, [abortAll, finishLoading]);

  const clickHandler = () => {
    showFileUpload();
  };

  return (
    <Component {...props} onClick={clickHandler}>
      <IonIcon icon={cameraOutline} slot="start" />
      <IonLabel>{attCollection.attachments.length > 0 ? t("g.upload-more") : t("g.upload")}</IonLabel>
    </Component>
  );
};
